import React, { useState, useEffect, useCallback } from 'react';
import { expenseAPI } from '../../../services';
import {
  Receipt,
  Clock,
  CheckCircle,
  XCircle,
  IndianRupee,
  Filter,
  RefreshCw,
  Eye,
  X
} from 'lucide-react';

const CATEGORY_LABELS = {
  travel: 'Travel',
  food: 'Food & Beverages',
  accommodation: 'Accommodation',
  office_supplies: 'Office Supplies',
  communication: 'Communication',
  medical: 'Medical',
  training: 'Training',
  client_entertainment: 'Client Entertainment',
  fuel: 'Fuel',
  miscellaneous: 'Miscellaneous'
};

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800'
};

const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const formatAmount = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

const ExpensesPage = () => {
  const [expenses, setExpenses] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [expRes, statsRes] = await Promise.all([
        expenseAPI.getAllExpenses(statusFilter ? { status: statusFilter } : {}),
        expenseAPI.getExpenseStats()
      ]);
      const expData = expRes?.data;
      setExpenses(Array.isArray(expData) ? expData : []);
      setStats(statsRes?.data || null);
    } catch {
      showToast('Failed to load expenses', 'error');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleAction = async (action) => {
    if (!selected) return;
    try {
      setActionLoading(true);
      if (action === 'approve') {
        await expenseAPI.approveExpense(selected._id, { adminNotes });
        showToast('Expense approved');
      } else {
        await expenseAPI.rejectExpense(selected._id, { adminNotes });
        showToast('Expense rejected');
      }
      setSelected(null);
      setAdminNotes('');
      load();
    } catch {
      showToast('Action failed', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-white text-sm ${toast.type === 'error' ? 'bg-red-500' : 'bg-green-500'}`}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Receipt className="w-6 h-6 text-blue-600" /> Expense Management
          </h1>
          <p className="text-gray-500 text-sm mt-1">Review and approve employee expense claims</p>
        </div>
        <button onClick={load} className="flex items-center gap-2 px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Pending', value: stats.pending, icon: Clock, color: 'text-yellow-600', bg: 'bg-yellow-50' },
            { label: 'Approved', value: stats.approved, icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50' },
            { label: 'Rejected', value: stats.rejected, icon: XCircle, color: 'text-red-600', bg: 'bg-red-50' },
            { label: 'Total Approved', value: formatAmount(stats.totalApprovedAmount), icon: IndianRupee, color: 'text-blue-600', bg: 'bg-blue-50', isAmount: true }
          ].map(({ label, value, icon: Icon, color, bg }) => (
            <div key={label} className={`${bg} rounded-xl p-4 flex items-center gap-3`}>
              <Icon className={`w-8 h-8 ${color}`} />
              <div>
                <p className="text-xs text-gray-500">{label}</p>
                <p className={`text-lg font-bold ${color}`}>{value}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-3 mb-4">
        <Filter className="w-4 h-4 text-gray-400" />
        {['', 'pending', 'approved', 'rejected'].map(s => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${statusFilter === s ? 'bg-blue-600 text-white' : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-50'}`}
          >
            {s === '' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-20 text-gray-400">Loading...</div>
        ) : expenses.length === 0 ? (
          <div className="flex flex-col items-center py-16 text-gray-400">
            <Receipt className="w-12 h-12 mb-3 opacity-30" />
            <p>No expense claims found</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['Employee', 'Title', 'Category', 'Amount', 'Date', 'Status', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {expenses.map(exp => (
                <tr key={exp._id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900">{exp.employee?.firstName} {exp.employee?.lastName}</p>
                    <p className="text-xs text-gray-400">{exp.employee?.employeeId}</p>
                  </td>
                  <td className="px-4 py-3 text-gray-700">{exp.title}</td>
                  <td className="px-4 py-3 text-gray-600">{CATEGORY_LABELS[exp.category] || exp.category}</td>
                  <td className="px-4 py-3 font-semibold text-gray-900">{formatAmount(exp.amount)}</td>
                  <td className="px-4 py-3 text-gray-600">{formatDate(exp.expenseDate)}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${STATUS_COLORS[exp.status]}`}>
                      {exp.status.charAt(0).toUpperCase() + exp.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => { setSelected(exp); setAdminNotes(exp.adminNotes || ''); }}
                      className="flex items-center gap-1 px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded"
                    >
                      <Eye className="w-3 h-3" /> View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Detail Modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">Expense Details</h2>
              <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><p className="text-gray-400 text-xs">Employee</p><p className="font-medium">{selected.employee?.firstName} {selected.employee?.lastName}</p></div>
                <div><p className="text-gray-400 text-xs">Employee ID</p><p className="font-medium">{selected.employee?.employeeId}</p></div>
                <div><p className="text-gray-400 text-xs">Title</p><p className="font-medium">{selected.title}</p></div>
                <div><p className="text-gray-400 text-xs">Category</p><p className="font-medium">{CATEGORY_LABELS[selected.category]}</p></div>
                <div><p className="text-gray-400 text-xs">Amount</p><p className="font-bold text-green-700">{formatAmount(selected.amount)}</p></div>
                <div><p className="text-gray-400 text-xs">Date</p><p className="font-medium">{formatDate(selected.expenseDate)}</p></div>
                <div className="col-span-2"><p className="text-gray-400 text-xs">Status</p>
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${STATUS_COLORS[selected.status]}`}>
                    {selected.status.charAt(0).toUpperCase() + selected.status.slice(1)}
                  </span>
                </div>
                {selected.description && (
                  <div className="col-span-2"><p className="text-gray-400 text-xs">Description</p><p>{selected.description}</p></div>
                )}
                {selected.receipt?.url && (
                  <div className="col-span-2">
                    <p className="text-gray-400 text-xs mb-1">Receipt</p>
                    <a href={selected.receipt.url} target="_blank" rel="noreferrer" className="text-blue-600 underline text-xs">View Receipt</a>
                  </div>
                )}
                {selected.adminNotes && (
                  <div className="col-span-2"><p className="text-gray-400 text-xs">Admin Notes</p><p>{selected.adminNotes}</p></div>
                )}
              </div>

              {selected.status === 'pending' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
                    <textarea
                      value={adminNotes}
                      onChange={e => setAdminNotes(e.target.value)}
                      rows={2}
                      placeholder="Add notes for the employee..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={() => handleAction('approve')}
                      disabled={actionLoading}
                      className="flex-1 flex items-center justify-center gap-2 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-60"
                    >
                      <CheckCircle className="w-4 h-4" /> Approve
                    </button>
                    <button
                      onClick={() => handleAction('reject')}
                      disabled={actionLoading}
                      className="flex-1 flex items-center justify-center gap-2 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-60"
                    >
                      <XCircle className="w-4 h-4" /> Reject
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpensesPage;
